import type React from 'react';
import { useRef, useState } from 'react';
import TypingAnimation from './TypingAnimation';
import ContactModal from './ContactModal';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { useRAFScroll } from '../hooks/useRAFScroll';
import { mobileQuery } from '../utils/mediaQueries';
import './PoweringDemand.css';

const PARALLAX_SPEEDS = { heading: 0.10, body: 0.14, cta: 0.18, partners: 0.22 } as const;
const PARALLAX_SPEEDS_MOBILE = { heading: 0, body: 0.10, cta: 0.13, partners: 0.16 } as const;

export default function PoweringDemand(): React.JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const ctaRef = useRef<HTMLButtonElement>(null);
  const partnersRef = useRef<HTMLDivElement>(null);

  const sectionRef = useIntersectionObserver<HTMLElement>(
    (entry) => entry.target.classList.add('powering--zooming'),
    { threshold: 0.1 },
  );

  useRAFScroll(() => {
    const el = sectionRef.current;
    if (!el) return;
    const { top } = el.getBoundingClientRect();
    const delta = window.innerHeight / 2 - (top + el.offsetHeight / 2);
    const speeds = mobileQuery.matches ? PARALLAX_SPEEDS_MOBILE : PARALLAX_SPEEDS;

    if (headingRef.current) headingRef.current.style.transform = `translateY(${delta * -speeds.heading}px)`;
    if (bodyRef.current) bodyRef.current.style.transform = `translateY(${delta * -speeds.body}px)`;
    if (ctaRef.current) ctaRef.current.style.transform = `translateY(${delta * -speeds.cta}px)`;
    if (partnersRef.current) partnersRef.current.style.transform = `translateY(${delta * -speeds.partners}px)`;
  });

  return (
    <section
      id="powering-section"
      className="powering"
      ref={sectionRef}
      aria-labelledby="powering-heading"
    >
      <div aria-hidden="true">
        <img src="/powering-bg.webp" alt="" className="powering__bg-img" fetchPriority="high" />
      </div>
      <div className="powering__overlay" />
      <div className="powering__content">
        <h2 id="powering-heading" className="powering__heading" ref={headingRef}>
          <TypingAnimation startOnView>Powering the next generation of demand</TypingAnimation>
        </h2>
        <p className="powering__body" ref={bodyRef}>
          Reliable. Dispatchable. Scalable. Get in touch today to explore partnership opportunities.
        </p>
        {false && (
          <button type="button" className="powering__cta" ref={ctaRef} onClick={() => setModalOpen(true)}>
            Get in touch
          </button>
        )}
        <div className="powering__partners" ref={partnersRef}>
          <p className="powering__partners-label">Our Partners</p>
          <div className="powering__partners-logos">
            <a href="https://roksolid.au/" target="_blank" rel="noopener noreferrer">
              <img src="/roksolidlogo.png" alt="Roksolid" className="powering__partner-logo powering__partner-logo--roksolid" />
            </a>
            <a href="https://baselineprojects.com.au/" target="_blank" rel="noopener noreferrer">
              <img src="/baseline-logo.svg" alt="Baseline" className="powering__partner-logo powering__partner-logo--baseline" />
            </a>
          </div>
        </div>
      </div>
      <ContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}
